import KCustomerAddressCard from "kinnect-front/KCustomers/KCustomerAddressCard";
import OutboundFreightDeleteDialog from "./OutboundFreightDeleteDialog";
import { Crudable } from "kinnect-mixins";
import {
	KActionDialog,
	KAddressMapDialog,
	KDataTable,
	KDataTableMenu,
	KFullscreenDialog,
	KLink,
	KStatusStepper
} from "kinnect-general";
import { OutboundFreightStatuses } from "kinnect-models";
import {
	VBtn,
	VCard,
	VCol,
	VContainer,
	VIcon,
	VRow
} from "vuetify/lib";
import { formatDate } from "kinnect-formatters";

export default KDataTable.extend( {
	name: "OutboundFreightTable",

	mixins: [ Crudable ],

	props: {
		editLoadTime: {
			default: true,
			type: Boolean
		},

		linkAddress: {
			default: true,
			type: Boolean
		},

		noDataText: {
			...KDataTable.options.props.noDataText,
			default: "There is no outbound freight to display"
		},

		noResultsText: {
			...KDataTable.options.props.noResultsText,
			default: "No matching outbound freight found"
		},

		sortBy: {
			...KDataTable.options.props.sortBy,
			default: "deliveryDate"
		},

		sortDesc: {
			...KDataTable.options.props.sortDesc,
			default: true
		},

		value: {
			type: Array,
			default: () => []
		}
	},

	data: () => ( {
		crud: "outbound_freight",
		statuses: [
			{
				text: OutboundFreightStatuses.ASSIGNED,
				value: "ASSIGNED"
			},
			{
				text: OutboundFreightStatuses.PICKING,
				value: "PICKING"
			},
			{
				text: OutboundFreightStatuses.PICKED,
				value: "PICKED"
			},
			{
				text: OutboundFreightStatuses.RCED,
				value: "RCED"
			},
			{
				text: OutboundFreightStatuses.INVOICED,
				value: "INVOICED"
			},
			{
				text: OutboundFreightStatuses.CHECKED,
				value: "CHECKED"
			},
			{
				text: OutboundFreightStatuses.SHIPPED,
				value: "SHIPPED"
			},
			{
				text: OutboundFreightStatuses.COMPLETED,
				value: "COMPLETED"
			}
		]
	} ),

	computed: {
		defaultHeaders() {
			return [
				{
					text: "Assigned to",
					value: "userAssigned.name"
				},
				{
					text: "Status",
					value: "statusText"
				},
				{
					text: "Est. Pickup",
					value: "estimatedPickup"
				},
				{
					text: "Est. Arrival",
					value: "estimatedArrivalDate"
				},
				{
					text: "Carrier",
					value: "carrier.name"
				},
				{
					text: "Tracking #",
					value: "trackingNumber"
				},
				{
					text: "Customer",
					value: "customer.name"
				},
				{
					text: "Address",
					value: "address.fullAddress"
				},
				{
					text: "Salesman",
					value: "salesman.user.name"
				},
				{
					text: "SO#",
					value: "salesOrderNumbers"
				},
				{
					text: "Desc",
					value: "description"
				},
				{
					align: "end",
					sortable: false,
					text: this.isMobile ? "Actions" : "",
					value: "actions"
				}
			];
		}
	},

	methods: {
		deleteItem( outboundFreight ) {
			return outboundFreight.delete().then( () => {
				this.removeOutboundFreightData( outboundFreight );
			} );
		},

		removeOutboundFreightData( outboundFreight ) {
			const deleteIndex = this.items.findIndex( item => outboundFreight.id === item.id );

			if ( deleteIndex !== -1 ) {
				this.$delete( this.items, deleteIndex );
			}

			this.$emit( "outboundFreightRemoved", outboundFreight );
		},

		updateStatus( item, status ) {
			item.status = status;

			return item.save();
		},

		genItemStatusStepper( item ) {
			if ( item.$.status === "UNAPPROVED" ) {
				return;
			}

			return <KStatusStepper
				loading={this.$wait.is( "save outbound freight" )}
				modelStatus={item.$.status}
				modelStatuses={item.statuses}
				on={{ "update:modelStatus": status => this.updateStatus( item, status ) }}
				readonly={!this.canEdit}
				requiresSignature={true}
				statuses={this.statuses}
				title={null}
			></KStatusStepper>;
		},

		genApproveRejectDialogs( item ) {
			if ( item.$.status !== "UNAPPROVED" || !this.canEdit ) {
				return;
			}

			return [
				<KActionDialog
					confirmButtonColor="success"
					confirmButtonText="Approve"
					loading={this.$wait.is( "save outbound freight" )}
					onConfirm={() => this.updateStatus( item, "ASSIGNED" )}
					scopedSlots={{
						default: () => "Are you sure you want to approve this outbound freight?",

						activator: ( { on } ) =>
							<VBtn
								block
								class="btn--text-left mb-2"
								color="success"
								depressed
								large
								on={on}
							>
								<VIcon left>thumb_up</VIcon> Approve
							</VBtn>
					}}
					title="Approve Request"
				></KActionDialog>,

				<KActionDialog
					confirmButtonColor="error"
					confirmButtonText="Reject and Delete"
					loading={this.$wait.is( "delete outbound freight" )}
					onConfirm={() => this.deleteItem( item )}
					scopedSlots={{
						default: () => "Are you sure you want to reject this outbound freight?",

						activator: ( { on } ) =>
							<VBtn
								block
								class="btn--text-left mb-2"
								color="error"
								depressed
								large
								on={on}
							>
								<VIcon left>thumb_down</VIcon> Reject
							</VBtn>
					}}
					title="Reject Request"
				></KActionDialog>
			];
		},

		genItemActions( item ) {
			return <KDataTableMenu closeOnContentClick={false}>
				<VCard dark>
					<VContainer fluid>
						<VRow>
							<VCol>
								{this.genApproveRejectDialogs( item )}

								<VBtn
									block
									class="btn--text-left mb-2"
									depressed
									large
									to={{
										name: "outboundFreight",
										params: { idOutboundFreight: item.id }
									}}
								><VIcon left>visibility</VIcon> View</VBtn>

								{this.canDelete
									&& !item.$.status !== "COMPLETED"
									&& <OutboundFreightDeleteDialog
										item={item}
										onConfirm={() => this.deleteItem( item )}
										scopedSlots={{
											activator: ( { on } ) =>
												<VBtn
													block
													class="btn--text-left"
													depressed
													large
													on={on}
												>
													<VIcon left>delete</VIcon> Delete
												</VBtn>
										}}
									></OutboundFreightDeleteDialog>
								}
							</VCol>
						</VRow>
					</VContainer>
				</VCard>
			</KDataTableMenu>;
		}
	},

	render() {
		return <KDataTable
			attrs={this.$props}
			headers={this.$_headers}
			on={{ ...this.$listeners }}
			vModel={this.internalValue}
			scopedSlots={{
				"item.statusText": ( { item } ) => (
					<KFullscreenDialog
						title="Update Outbound Freight Progress"
						scopedSlots={{
							activator: ( { on } ) =>
								<KLink on={on}>{item.statusText}</KLink>
						}}
					>
						<OutboundFreightTable
							class="px-4 mb-4"
							dense
							disableFiltering
							disablePagination
							disableSort
							hiddenCols={[
								"actions",
								"statusText",
								"userAssigned.name"
							]}
							hideDefaultFooter
							items={[ item ]}
							itemsPerPage={-1}
						></OutboundFreightTable>

						{this.genItemStatusStepper( item )}
					</KFullscreenDialog>
				),

				"item.estimatedPickup": ( { item } ) => (
					item.estimatedPickup
						? <span class="text-no-wrap">
							{item.estimatedPickup}
						</span>
						: "--"
				),

				"item.estimatedArrivalDate": ( { item } ) => (
					item.estimatedArrivalDate
						? <span class="text-no-wrap">
							{formatDate( item.estimatedArrivalDate, "MMM d" )}
						</span>
						: "--"
				),

				"item.customer.name": ( { item } ) => (
					item.customer
						? <KCustomerAddressCard
							address={item.address}
							customer={item.customer}
						></KCustomerAddressCard>
						: "--"
				),

				"item.address.fullAddress": ( { item } ) => {
					if ( !item.address ) {
						return "--";
					}

					return this.linkAddress
						? <KAddressMapDialog address={item.address}></KAddressMapDialog>
						: item.address.fullAddress;
				},

				"item.actions": ( { item } ) => this.genItemActions( item ),

				...this.$scopedSlots
			}}
		></KDataTable>;
	}
} );
